/* eslint-disable class-methods-use-this */
import Config from "@/config/Config";
import { EventParticipant } from "@models/EventParticipant";
// eslint-disable-next-line object-curly-newline
import {
  getFirestore,
  setDoc,
  getDoc,
  doc,
  onSnapshot,
  query,
  collection,
  where,
  DocumentData,
  DocumentSnapshot,
  Unsubscribe,
  getDocs,
} from "firebase/firestore";
import { sortUtils } from "@/utils/sortUtils";
import { getFunctions, httpsCallable } from "firebase/functions";

class EventParticipantDataService {
  eventCollectionName = "events";

  collectionName = "participants";

  async save(eventParticipant: EventParticipant): Promise<EventParticipant> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventParticipant.eventId);
    const eventParticipantDoc = doc(eventDoc, this.collectionName, eventParticipant.id as string);

    await setDoc(
      eventParticipantDoc,
      {
        ...(eventParticipant.name ? { name: eventParticipant.name } : {}),
        stops: eventParticipant.stops,
        checkedIn: eventParticipant.checkedIn,
      },
      { merge: true }
    );
    return eventParticipant;
  }

  
  async getAll(eventId: string): Promise<EventParticipant[]> {
    const db = getFirestore();

    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const eventParticipantRefs = query(
      collection(eventDoc, "participants")
    );

    const data = await getDocs(eventParticipantRefs);
    const results: EventParticipant[] = [];

    data.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as EventParticipant);
    });
    return results;
  }

  async get(eventId: string, userId: string): Promise<EventParticipant[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const participantsQuery = query(
      collection(eventDoc, "participants"),
      // eslint-disable-next-line comma-dangle
      where("userId", "==", `${userId}`)
    );

    const data = await getDocs(participantsQuery);
    const results: EventParticipant[] = [];

    const participantPromises: Promise<DocumentSnapshot<DocumentData>>[] = [];
    data.forEach((item) => {
      participantPromises.push(getDoc(item.ref));
    });

    const participantDocumentSnapshots = await Promise.all(participantPromises);
    participantDocumentSnapshots.forEach((participantDoc) => {
      results.push({ ...participantDoc.data(), id: participantDoc.id } as EventParticipant);
    });
    //use sortbyTimestampandId from sort utils to sort participants
    results.sort(sortUtils.byTimestampAndId);
    //then set names by index if participant unnamed
    results.forEach((participant, index) => {
      if (!participant.name) {
        participant.name = `Hand ${index + 1}`;
      }
    });
    return results;
  }

  async getSubscription(
    eventId: string,
    participantId: string,
    handleEventParticipantChange: (eventParticipant: EventParticipant, changeType: string) => void
  ): Promise<Unsubscribe | null> {
    const db = getFirestore();
  
    const eventDoc = doc(db, this.eventCollectionName, eventId);
  
    const participantsQuery = query(
      collection(eventDoc, "participants"),
      // eslint-disable-next-line comma-dangle
      where("userId", "==", `${participantId}`)
    );
  
    const unsubscribe = onSnapshot(
      participantsQuery,
      (snapshot) => {
        const results: EventParticipant[] = [];
        
        snapshot.docChanges().forEach((change) => {
          const participant = {
            id: change.doc.id,
            eventId,
            ...change.doc.data(),
          } as unknown as EventParticipant;
          
          results.push(participant);
        });
        
        // Apply the same sorting as in the get method
        results.sort(sortUtils.byTimestampAndId);
        
        // Set names by index if participant unnamed
        results.forEach((participant, index) => {
          if (!participant.name) {
            participant.name = `Hand ${index + 1}`;
          }
        });
        
        // Now notify about each change with properly named participants
        results.forEach(participant => {
          const changeType = snapshot.docChanges().find(change => change.doc.id === participant.id)?.type || 'modified';
          handleEventParticipantChange(participant, changeType);
        });
      },
      (error) => {
        console.error("Error:", error);
      }
    );
  
    return unsubscribe;
  }

  async isRegisteredByPhoneNumber(eventId: string, phoneNumber: string): Promise<boolean> {
    const functions = getFunctions();
    const isRegistered = httpsCallable(functions, Config.functions.IsRegistered);
    const result = (await isRegistered({
      eventId,
      phoneNumber,
    })) as any;

    if (result.data?.error) {
      throw result;
    }

    return result.data.isRegistered;
  }
}

export default new EventParticipantDataService();
